import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const POPOVER_WIDTH = 280;
const POPOVER_ESTIMATED_HEIGHT = 390;
const POPOVER_GAP = 6;
const VIEWPORT_MARGIN = 8;

export const getOvertimeUnitLabel = rows => {
  const units = new Set(
    (rows || []).map(row => String(row?.unit ?? '').trim()).filter(Boolean)
  );

  if (units.size === 1) return Array.from(units)[0];
  return units.size > 1 ? 'Mixed Units' : '';
};

const getPopoverPosition = button => {
  const rect = button?.getBoundingClientRect?.();
  if (!rect || typeof window === 'undefined') return null;

  const left = Math.min(
    Math.max(VIEWPORT_MARGIN, rect.right - POPOVER_WIDTH),
    Math.max(
      VIEWPORT_MARGIN,
      window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN
    )
  );

  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
  const top =
    spaceBelow >= POPOVER_ESTIMATED_HEIGHT
      ? rect.bottom + POPOVER_GAP
      : Math.max(
          VIEWPORT_MARGIN,
          rect.top - POPOVER_ESTIMATED_HEIGHT - POPOVER_GAP
        );

  return {
    left,
    maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
    overflowY: 'auto',
    top,
    width: POPOVER_WIDTH,
  };
};

const getDraftSettings = column => ({
  label: column.overtimeLabel || '',
  multiplier: String(column.overtimeMultiplier ?? column.defaultMultiplier),
  start:
    column.overtimeStart === null || column.overtimeStart === undefined
      ? ''
      : String(column.overtimeStart),
  end:
    column.overtimeEnd === null || column.overtimeEnd === undefined
      ? ''
      : String(column.overtimeEnd),
});

const normalizeOptionalDecimal = value => {
  if (String(value ?? '').trim() === '') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? String(value).trim() : false;
};

const OvertimeHeader = ({ canEdit, column, onCommit, unitLabel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const [draft, setDraft] = useState(() => getDraftSettings(column));
  const [error, setError] = useState('');
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const heading = `${column.managerLabel}${unitLabel ? ` (${unitLabel})` : ''}`;

  useEffect(() => {
    if (!isOpen) setDraft(getDraftSettings(column));
  }, [column, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () =>
      setPopoverStyle(getPopoverPosition(buttonRef.current));
    const closeOnOutsideClick = event => {
      if (
        buttonRef.current?.contains(event.target) ||
        popoverRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
      setError('');
    };
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setError('');
      }
    };

    updatePosition();
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const updateDraft = key => event => {
    setDraft(current => ({ ...current, [key]: event.target.value }));
    setError('');
  };

  const saveSettings = () => {
    const multiplier = normalizeOptionalDecimal(draft.multiplier);
    const start = normalizeOptionalDecimal(draft.start);
    const end = normalizeOptionalDecimal(draft.end);

    if (multiplier === false || multiplier === null) {
      setError('Enter a non-negative multiplier.');
      return;
    }
    if (start === false || end === false) {
      setError('Cutoffs must be non-negative numbers or blank.');
      return;
    }
    if (start !== null && end !== null && Number(end) < Number(start)) {
      setError('The end cutoff must be greater than or equal to the start.');
      return;
    }

    onCommit(column.key, {
      label: draft.label.trim().slice(0, 80),
      multiplier,
      start,
      end,
    });
    setIsOpen(false);
    setError('');
  };

  const popover =
    isOpen && popoverStyle && typeof document !== 'undefined'
      ? createPortal(
          <div
            aria-label={`${column.managerLabel} settings`}
            className="overtime-header-popover"
            onMouseDown={event => event.stopPropagation()}
            onPointerDown={event => event.stopPropagation()}
            ref={popoverRef}
            role="dialog"
            style={popoverStyle}
          >
            <div className="overtime-header-popover__title">
              <strong>{column.managerLabel} settings</strong>
              <button
                aria-label={`Close ${column.managerLabel} settings`}
                onClick={() => setIsOpen(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <label className="overtime-header-popover__field">
              <span>Label</span>
              <input
                disabled={!canEdit}
                maxLength={80}
                onChange={updateDraft('label')}
                placeholder="Overtime label"
                type="text"
                value={draft.label}
              />
            </label>
            <label className="overtime-header-popover__field">
              <span>Rate multiplier</span>
              <input
                disabled={!canEdit}
                inputMode="decimal"
                onChange={updateDraft('multiplier')}
                placeholder={column.defaultMultiplier}
                type="text"
                value={draft.multiplier}
              />
            </label>
            <div className="overtime-header-popover__cutoffs">
              <label className="overtime-header-popover__field">
                <span>Start cutoff</span>
                <input
                  disabled={!canEdit}
                  inputMode="decimal"
                  onChange={updateDraft('start')}
                  placeholder="e.g. 8"
                  type="text"
                  value={draft.start}
                />
              </label>
              <label className="overtime-header-popover__field">
                <span>End cutoff</span>
                <input
                  disabled={!canEdit}
                  inputMode="decimal"
                  onChange={updateDraft('end')}
                  placeholder="e.g. 12"
                  type="text"
                  value={draft.end}
                />
              </label>
            </div>
            <p className="overtime-header-popover__help">
              These settings apply to every row in this budget.
            </p>
            {error && (
              <p className="overtime-header-popover__error" role="alert">
                {error}
              </p>
            )}
            {canEdit && (
              <div className="overtime-header-popover__actions">
                <button onClick={() => setIsOpen(false)} type="button">
                  Cancel
                </button>
                <button onClick={saveSettings} type="button">
                  Save
                </button>
              </div>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div className="overtime-header">
      <span className="overtime-header__copy">
        <span className="overtime-header__title" title={heading}>
          {heading}
        </span>
        <small>{column.overtimeLabel}</small>
      </span>
      <button
        aria-expanded={isOpen}
        aria-label={`Configure ${column.managerLabel}`}
        className="overtime-header__menu"
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen(open => !open);
        }}
        onMouseDown={event => event.stopPropagation()}
        onPointerDown={event => event.stopPropagation()}
        ref={buttonRef}
        title={`Configure ${column.managerLabel}`}
        type="button"
      >
        •••
      </button>
      {popover}
    </div>
  );
};

OvertimeHeader.propTypes = {
  canEdit: PropTypes.bool.isRequired,
  column: PropTypes.shape({
    defaultMultiplier: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    managerLabel: PropTypes.string.isRequired,
    overtimeEnd: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    overtimeLabel: PropTypes.string,
    overtimeMultiplier: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    overtimeStart: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onCommit: PropTypes.func.isRequired,
  unitLabel: PropTypes.string,
};

OvertimeHeader.defaultProps = {
  unitLabel: '',
};

export default OvertimeHeader;
